import { X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { replyPreview } from "@/chat/format"
import type { ReplySnapshot } from "@/chat/protocol"
import { cn } from "@/lib/utils"

/**
 * The quoted message: the rule down its leading edge, the author, and one
 * line of what they said. Used in two places — above a reply in the thread,
 * where it jumps to the original, and above the composer while a reply is
 * being written, where it can be taken back.
 *
 * The rule is `border-current` and the block has no fill of its own, so it
 * reads on your own bubble, on someone else's and on the composer alike. It
 * deliberately avoids an opacity modifier on a colour: `color-mix()` falls
 * back to the *solid* colour on Chrome 109, which would paint the text over
 * itself. A plain `opacity` layer is what dims it instead.
 */
export function ReplyQuote({
  snapshot,
  onJump,
  onCancel,
  className,
}: {
  snapshot: ReplySnapshot
  /** Scrolls the thread to the quoted message. */
  onJump?: () => void
  /** Drops the reply being composed. */
  onCancel?: () => void
  className?: string
}) {
  const preview = replyPreview(snapshot)

  const body = (
    <>
      <span className="max-w-full truncate text-xs font-semibold">
        {snapshot.name}
      </span>
      <span className="max-w-full truncate text-xs">
        {preview || "Сообщение"}
      </span>
    </>
  )

  return (
    <div
      className={cn(
        "flex max-w-full items-stretch gap-2 border-s-2 border-current py-0.5 ps-2",
        className
      )}
    >
      {onJump ? (
        <button
          type="button"
          onClick={onJump}
          aria-label={`Перейти к сообщению от ${snapshot.name}`}
          className="flex min-w-0 flex-1 flex-col items-start text-start opacity-80 outline-none hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring"
        >
          {body}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col items-start opacity-80">
          {body}
        </div>
      )}

      {onCancel && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onCancel}
          aria-label="Отменить ответ"
          className="-me-0.5 self-center"
        >
          <X />
        </Button>
      )}
    </div>
  )
}
